import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { LoadingRows } from '@/components/skeletons'
import { api } from '@/api/client'
import { Page } from '@/components/page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { STATUS_LABELS, useProjects } from '@/features/projects/api'
import { formatDate } from '@/lib/format'

export const Route = createFileRoute('/projects/')({ component: ProjectsPage })

function ProjectsPage() {
  const [includeArchived, setIncludeArchived] = useState(false)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const queryClient = useQueryClient()
  const projects = useProjects(includeArchived)

  const create = useMutation({
    mutationFn: () => api.POST('/api/projects', { body: { name: name.trim() } }),
    onSuccess: async () => {
      setCreating(false)
      setName('')
      toast.success('Project created')
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not create'),
  })

  const rows = projects.data ?? []

  return (
    <Page
      title="Projects"
      description="Bills of material and builds."
      actions={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon />
          New project
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex w-fit items-center gap-2 text-sm">
          <Checkbox
            checked={includeArchived}
            onCheckedChange={(checked) => setIncludeArchived(checked === true)}
          />
          Show archived projects
        </label>

        {projects.isPending ? (
          <LoadingRows label="Loading projects" />
        ) : projects.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the projects</AlertTitle>
            <AlertDescription>
              {projects.error instanceof Error ? projects.error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground rounded-lg border border-dashed p-10 text-center text-sm">
            No projects yet.
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: project.id }}
                        className="hover:text-primary font-medium"
                      >
                        {project.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={project.status === 'archived' ? 'muted' : 'outline'}>
                        {STATUS_LABELS[project.status] ?? project.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {project.description || '—'}
                    </TableCell>
                    <TableCell>{formatDate(project.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button disabled={create.isPending || !name.trim()} onClick={() => create.mutate()}>
              {create.isPending && <Spinner />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
